// Shared toast primitive — promoted from ChartApp.js's local showToast.
//
// API:
//   import { toast, ToastHost } from 'components/ui/toast';
//   toast.success('Booking cancelled — your client was notified.');
//   toast.error("Couldn't mark complete — check your connection and try again.");
//
// Visual: warm-ink chip (bg-ink, cream text), rounded-btn, shadow-atelier-lg,
// fixed bottom-center, auto-dismiss ~4s, stack max 3. Respects reduced motion
// (fade only — no slide). Hover pauses the dismissal timer.

import React, { useEffect, useState, useRef } from 'react';
import { Check, AlertCircle, Info } from 'lucide-react';

let _push = null;
let _id = 0;

const emit = (kind, message, opts = {}) => {
  if (!_push) {
    if (process.env.NODE_ENV !== 'production') {
      console.warn('[toast] no <ToastHost/> mounted — dropped:', message);
    }
    return null;
  }
  const id = ++_id;
  _push({ id, kind, message, duration: opts.duration ?? 4000 });
  return id;
};

export const toast = {
  success: (message, opts) => emit('success', message, opts),
  error:   (message, opts) => emit('error',   message, opts),
  info:    (message, opts) => emit('info',    message, opts),
  show:    (message, opts) => emit('info',    message, opts),
};

export function ToastHost() {
  const [toasts, setToasts] = useState([]);
  const timers = useRef({});
  const paused = useRef(false);

  useEffect(() => {
    _push = (t) => {
      setToasts((prev) => {
        const next = [...prev, t];
        return next.length > 3 ? next.slice(next.length - 3) : next;
      });
      armTimer(t.id, t.duration);
    };
    return () => { _push = null; };
  }, []);

  const armTimer = (id, duration) => {
    if (timers.current[id]) clearTimeout(timers.current[id]);
    timers.current[id] = setTimeout(() => {
      setToasts((prev) => prev.filter((x) => x.id !== id));
      delete timers.current[id];
    }, duration);
  };

  const pauseAll = () => {
    paused.current = true;
    Object.values(timers.current).forEach(clearTimeout);
  };
  const resumeAll = () => {
    if (!paused.current) return;
    paused.current = false;
    toasts.forEach((t) => armTimer(t.id, 2000));
  };

  const remove = (id) => {
    if (timers.current[id]) {
      clearTimeout(timers.current[id]);
      delete timers.current[id];
    }
    setToasts((prev) => prev.filter((x) => x.id !== id));
  };

  if (toasts.length === 0) return null;

  return (
    <div
      onMouseEnter={pauseAll}
      onMouseLeave={resumeAll}
      style={{
        position: 'fixed',
        left: '50%',
        bottom: 'calc(env(safe-area-inset-bottom) + 6rem)',
        transform: 'translateX(-50%)',
        zIndex: 100,
        display: 'flex',
        flexDirection: 'column',
        gap: '0.5rem',
        pointerEvents: 'none',
        maxWidth: 'calc(100vw - 2rem)',
      }}
      aria-live="polite"
      aria-atomic="true"
    >
      {toasts.map((t) => (
        <ToastChip key={t.id} t={t} onDismiss={() => remove(t.id)} />
      ))}
    </div>
  );
}

function ToastChip({ t, onDismiss }) {
  const Icon = t.kind === 'success' ? Check : t.kind === 'error' ? AlertCircle : Info;
  const iconColor =
    t.kind === 'success' ? '#A9C39A' :
    t.kind === 'error'   ? '#E0A09C' :
    'rgba(246,241,232,0.7)';
  return (
    <div
      role="status"
      style={{
        background: 'var(--ink)',
        color: 'var(--bg)',
        borderRadius: 'var(--radius-btn)',
        boxShadow: 'var(--shadow-lg)',
        padding: '0.625rem 0.875rem',
        fontSize: '0.875rem',
        lineHeight: 1.35,
        display: 'flex',
        alignItems: 'center',
        gap: '0.625rem',
        pointerEvents: 'auto',
        animation: 'avayble-toast-in 220ms ease-out',
        maxWidth: '24rem',
      }}
    >
      <Icon size={16} style={{ color: iconColor, flexShrink: 0 }} />
      <span style={{ flex: 1, minWidth: 0 }}>{t.message}</span>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss notification"
        style={{
          background: 'transparent',
          border: 'none',
          color: 'rgba(246,241,232,0.5)',
          cursor: 'pointer',
          padding: 0,
          marginLeft: '0.25rem',
          fontSize: '0.875rem',
          lineHeight: 1,
        }}
      >
        ×
      </button>
    </div>
  );
}

export default toast;
